import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ShieldCheck, LogOut, ArrowLeft, Download, Globe, Trash2, FileDown, Clock3, Upload, PackageCheck, Power } from "lucide-react";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";
const SITE_LIVE_SINCE = "2026-09-02T13:56:41+05:30";

const formatIST = (ts) =>
  new Date(ts).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const browserInfo = (ua = "") => {
  let browser = "Other";
  if (/edg\//i.test(ua)) browser = "Edge";
  else if (/opr\/|opera/i.test(ua)) browser = "Opera";
  else if (/chrome\//i.test(ua)) browser = "Chrome";
  else if (/firefox\//i.test(ua)) browser = "Firefox";
  else if (/safari\//i.test(ua)) browser = "Safari";
  let os = "";
  if (/windows/i.test(ua)) os = "Windows";
  else if (/android/i.test(ua)) os = "Android";
  else if (/iphone|ipad/i.test(ua)) os = "iOS";
  else if (/mac os/i.test(ua)) os = "macOS";
  else if (/linux/i.test(ua)) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
};

const formatLiveDuration = (ts, now) => {
  const startedAt = new Date(ts).getTime();
  if (Number.isNaN(startedAt)) return "—";
  const totalSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock = [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
  return days ? `${days}d ${clock}` : clock;
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const TwoFactorForm = ({ code, setCode, loading, error, onSubmit, buttonText }) => (
  <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="admin-2fa-form">
    <input type="text" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="one-time-code" inputMode="text" maxLength={20} placeholder="6-DIGIT CODE OR RECOVERY CODE" data-testid="admin-2fa-code" className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm tracking-[0.18em] uppercase placeholder:text-[10px] placeholder:text-muted-foreground focus:outline-none focus:border-accent" />
    {error && <div className="font-mono text-xs tracking-[0.1em] text-red-500">{error}</div>}
    <button type="submit" disabled={loading} data-testid="admin-2fa-submit" className="w-fit px-8 py-3 bg-accent text-accent-foreground font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">{loading ? "Verifying..." : buttonText}</button>
  </form>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("yiis-admin-token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authStep, setAuthStep] = useState("password");
  const [preAuthToken, setPreAuthToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingAccessToken, setPendingAccessToken] = useState("");
  const [data, setData] = useState(null);
  const [installerStatus, setInstallerStatus] = useState(null);
  const [installerFile, setInstallerFile] = useState(null);
  const [installerBusy, setInstallerBusy] = useState(false);
  const [installerMessage, setInstallerMessage] = useState("");
  const [siteAccess, setSiteAccess] = useState(null);
  const [siteAccessBusy, setSiteAccessBusy] = useState(false);
  const [siteAccessMessage, setSiteAccessMessage] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [activeLog, setActiveLog] = useState("downloads");

  useEffect(() => {
    document.documentElement.classList.toggle(
      "dark",
      (localStorage.getItem("yiis-theme") || "dark") === "dark"
    );
  }, []);

  const loadDownloads = useCallback(async (tok) => {
    if (!API) {
      setError("Admin service is not configured.");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${tok}` };
      const [downloadsRes, installerRes, siteAccessRes] = await Promise.all([
        axios.get(`${API}/admin/downloads`, { headers }),
        axios.get(`${API}/admin/installer`, { headers }),
        axios.get(`${API}/admin/site-access`, { headers }),
      ]);
      setData(downloadsRes.data);
      setInstallerStatus(installerRes.data);
      setSiteAccess(siteAccessRes.data);
    } catch (e) {
      if ([401, 403].includes(e.response?.status)) {
        localStorage.removeItem("yiis-admin-token");
        setToken("");
        setData(null);
        setInstallerStatus(null);
        setSiteAccess(null);
      } else {
        setError("Admin service is temporarily unavailable.");
      }
    }
  }, []);

  useEffect(() => {
    if (token) loadDownloads(token);
  }, [token, loadDownloads]);

  useEffect(() => {
    if (!token || !data) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [token, data]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (!API) {
      setError("Admin service is not configured.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.post(API + "/admin/login", { email, password });
      if (res.data.requires_2fa_setup) {
        setPreAuthToken(res.data.setup_token);
        setTwoFactorSetup({ qrCode: res.data.qr_code, manualKey: res.data.manual_key });
        setAuthStep("setup");
      } else if (res.data.requires_2fa) {
        setPreAuthToken(res.data.challenge_token);
        setAuthStep("verify");
      } else {
        throw new Error("Unexpected authentication response");
      }    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactor = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = authStep === "setup" ? "confirm" : "verify";
      const res = await axios.post(
        API + "/admin/2fa/" + endpoint,
        { code: twoFactorCode },
        { headers: { Authorization: "Bearer " + preAuthToken } }
      );
      if (res.data.recovery_codes?.length) {
        setRecoveryCodes(res.data.recovery_codes);
        setPendingAccessToken(res.data.token);
        setAuthStep("recovery");
      } else {
        localStorage.setItem("yiis-admin-token", res.data.token);
        setToken(res.data.token);
      }
      setTwoFactorCode("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Two-factor verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const finishTwoFactorSetup = () => {
    localStorage.setItem("yiis-admin-token", pendingAccessToken);
    setToken(pendingAccessToken);
    setPendingAccessToken("");
    setRecoveryCodes([]);
  };

  const resetAuthentication = () => {
    setAuthStep("password");
    setPreAuthToken("");
    setTwoFactorCode("");
    setTwoFactorSetup(null);
    setRecoveryCodes([]);
    setPendingAccessToken("");
    setPassword("");
    setError("");
  };

  const logout = () => {
    localStorage.removeItem("yiis-admin-token");
    setToken("");
    setData(null);
    setInstallerStatus(null);
    setInstallerFile(null);
    setInstallerMessage("");
    setSiteAccess(null);
    setSiteAccessMessage("");
    resetAuthentication();
  };

  const toggleSiteAccess = async () => {
    if (!siteAccess || siteAccessBusy) return;
    const nextMaintenanceMode = !siteAccess.maintenance_mode;
    setSiteAccessBusy(true);
    setSiteAccessMessage("");
    try {
      const res = await axios.post(
        `${API}/admin/site-access`,
        {
          maintenance_mode: nextMaintenanceMode,
          message: siteAccess.message,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSiteAccess(res.data);
      setSiteAccessMessage(
        nextMaintenanceMode
          ? "Maintenance mode is ON. Public visitors now see the maintenance screen."
          : "Public access is ON. Everyone can visit the website again."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSiteAccessMessage(typeof detail === "string" ? detail : "Site access update failed. Please try again.");
    } finally {
      setSiteAccessBusy(false);
    }
  };

  const uploadInstaller = async (e) => {
    e.preventDefault();
    if (!installerFile || installerBusy) return;
    const formElement = e.currentTarget;
    setInstallerBusy(true);
    setInstallerMessage("");
    const form = new FormData();
    form.append("installer", installerFile);
    try {
      const res = await axios.post(`${API}/admin/installer`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInstallerStatus({ ...res.data, max_bytes: installerStatus?.max_bytes });
      setInstallerFile(null);
      formElement.reset();
      setInstallerMessage("Installer uploaded successfully. Public downloads are now available.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInstallerMessage(typeof detail === "string" ? detail : "Installer upload failed. Please try again.");
    } finally {
      setInstallerBusy(false);
    }
  };

  const deleteInstaller = async () => {
    if (installerBusy || !window.confirm("Delete the uploaded installer? Public downloads will become unavailable.")) return;
    setInstallerBusy(true);
    setInstallerMessage("");
    try {
      const res = await axios.delete(`${API}/admin/installer`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInstallerStatus((current) => ({
        ...current,
        available: res.data.available,
        source: res.data.source,
        size: null,
        sha256: "",
        uploaded_at: "",
      }));
      setInstallerMessage(res.data.available
        ? "Uploaded installer deleted; another configured installer source remains active."
        : "Installer deleted. Public downloads are now unavailable.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInstallerMessage(typeof detail === "string" ? detail : "Installer deletion failed. Please try again.");
    } finally {
      setInstallerBusy(false);
    }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/export/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yiis-100-${kind}-log.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      /* export failed silently */
    }
  };

  const clearLogs = async () => {
    if (!window.confirm("Delete ALL download, visitor and maintenance-attempt logs? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/admin/clear-logs`, {}, { headers: { Authorization: `Bearer ${token}` } });
      loadDownloads(token);
    } catch (e) {
      /* keep current data */
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans px-6 md:px-12 py-10" data-testid="admin-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-16">
          <a
            href="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to site
          </a>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
            YKD ENTERPRISES / ADMIN
          </span>
        </div>

        {!token || !data ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md"
          >
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck size={20} className="text-accent" />
              <h1 className="font-display font-semibold tracking-tight text-3xl md:text-4xl" data-testid="admin-login-heading">
                ADMIN ACCESS
              </h1>
            </div>
            {authStep === "password" && (
              <>
                <p className="text-muted-foreground text-sm leading-relaxed mb-8">Restricted area. Enter your admin password, then verify with your authenticator app.</p>
                <form onSubmit={handleLogin} className="flex flex-col gap-4" data-testid="admin-login-form">
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="ADMIN EMAIL" data-testid="admin-email-input" className="w-full bg-transparent border border-border px-4 py-3 font-mono text-xs tracking-[0.15em] normal-case placeholder:uppercase placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors duration-300" />
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="PASSWORD" data-testid="admin-password-input" className="w-full bg-transparent border border-border px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors duration-300" />
                  {error && <div className="font-mono text-xs tracking-[0.1em] text-red-500">{error}</div>}
                  <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-fit px-8 py-3 bg-accent text-accent-foreground font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-50">{loading ? "Verifying..." : "Continue"}</button>
                </form>
              </>
            )}

            {authStep === "setup" && (
              <>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">Scan this QR code in Google Authenticator, Microsoft Authenticator, Authy or another TOTP app.</p>
                <div className="w-fit bg-white p-3 mb-5"><img src={twoFactorSetup?.qrCode} alt="YIIS-100 admin authenticator QR code" className="w-48 h-48" /></div>
                <div className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground mb-6 break-all">Manual key: <span className="text-foreground select-all">{twoFactorSetup?.manualKey}</span></div>
                <TwoFactorForm code={twoFactorCode} setCode={setTwoFactorCode} loading={loading} error={error} onSubmit={handleTwoFactor} buttonText="Enable 2FA" />
                <button type="button" onClick={resetAuthentication} className="mt-5 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground hover:text-accent">Start Again</button>
              </>
            )}

            {authStep === "verify" && (
              <>
                <p className="text-muted-foreground text-sm leading-relaxed mb-8">Enter the current 6-digit authenticator code, or use one unused recovery code.</p>
                <TwoFactorForm code={twoFactorCode} setCode={setTwoFactorCode} loading={loading} error={error} onSubmit={handleTwoFactor} buttonText="Verify & Sign In" />
                <button type="button" onClick={resetAuthentication} className="mt-5 font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground hover:text-accent">Back to Password</button>
              </>
            )}

            {authStep === "recovery" && (
              <>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">Save these one-time recovery codes now. They will not be shown again.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-border p-5 mb-6 font-mono text-sm tracking-[0.12em] select-all">{recoveryCodes.map((code) => <div key={code}>{code}</div>)}</div>
                <button type="button" onClick={() => navigator.clipboard?.writeText(recoveryCodes.join(String.fromCharCode(10)))} className="mr-3 px-5 py-3 border border-border font-mono text-[10px] tracking-[0.18em] uppercase hover:border-accent hover:text-accent">Copy Codes</button>
                <button type="button" onClick={finishTwoFactorSetup} className="px-5 py-3 bg-accent text-accent-foreground font-mono text-[10px] tracking-[0.18em] uppercase font-bold">I Have Saved Them</button>
              </>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="admin-dashboard"
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <h1 className="font-display font-semibold tracking-tight text-3xl md:text-5xl" data-testid="admin-dashboard-heading">
                DOWNLOAD <span className="text-accent">ANALYTICS</span>
              </h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={clearLogs}
                  data-testid="admin-clear-logs-button"
                  className="w-fit flex items-center gap-2 px-5 py-2 border border-red-500/40 text-red-500 font-mono text-xs tracking-[0.2em] uppercase hover:bg-red-500 hover:text-white transition-colors duration-300"
                >
                  <Trash2 size={13} /> Clear Logs
                </button>
                <button
                  onClick={logout}
                  data-testid="admin-logout-button"
                  className="w-fit flex items-center gap-2 px-5 py-2 border border-border font-mono text-xs tracking-[0.2em] uppercase hover:border-accent hover:text-accent transition-colors duration-300"
                >
                  <LogOut size={13} /> Logout
                </button>
              </div>
            </div>

            <section className="border border-border mb-12" data-testid="admin-site-access-control">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-6 md:p-8">
                <div>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                    <Power size={13} className="text-accent" /> Website Access
                  </div>
                  <div className="font-display font-semibold text-2xl md:text-3xl" data-testid="admin-site-access-status">
                    {siteAccess?.maintenance_mode ? "Maintenance mode is ON" : "Public access is ON"}
                  </div>
                  <p className="font-mono text-xs text-muted-foreground mt-2 max-w-2xl leading-relaxed">
                    {siteAccess?.maintenance_mode
                      ? "Only a signed-in admin can preview the full website. Public visitors see the maintenance screen."
                      : "The website is open and available to all visitors."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleSiteAccess}
                  disabled={!siteAccess || siteAccessBusy}
                  data-testid="admin-site-access-toggle"
                  className={`${siteAccess?.maintenance_mode ? "bg-accent text-accent-foreground" : "border border-amber-500/60 text-amber-500"} shrink-0 flex items-center justify-center gap-2 px-6 py-3 font-mono text-xs tracking-[0.18em] uppercase font-bold transition-colors duration-300 disabled:opacity-40`}
                >
                  <Power size={14} />
                  {siteAccessBusy ? "Updating..." : siteAccess?.maintenance_mode ? "Turn Public On" : "Turn Maintenance On"}
                </button>
              </div>
              {siteAccessMessage && (
                <div className="px-6 md:px-8 py-4 border-t border-border font-mono text-xs tracking-[0.08em] text-muted-foreground" data-testid="admin-site-access-message">
                  {siteAccessMessage}
                </div>
              )}
            </section>

            <section className="border border-border mb-12" data-testid="admin-installer-manager">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-6 md:p-8 border-b border-border">
                <div>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                    <PackageCheck size={13} className="text-accent" /> Installer Management
                  </div>
                  <div className="font-display font-semibold text-2xl md:text-3xl" data-testid="admin-installer-status">
                    {installerStatus?.available ? "Installer available" : "Installer unavailable"}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground mt-2" data-testid="admin-installer-details">
                    {installerStatus?.available
                      ? `${installerStatus.filename} · ${formatBytes(installerStatus.size)}${installerStatus.uploaded_at ? ` · Uploaded ${formatIST(installerStatus.uploaded_at)} IST` : ""}`
                      : `Upload a valid Windows .exe file (${formatBytes(installerStatus?.max_bytes)} maximum).`}
                  </div>
                  {installerStatus?.sha256 && (
                    <div className="font-mono text-[10px] text-muted-foreground mt-2 break-all" data-testid="admin-installer-sha256">
                      SHA-256: {installerStatus.sha256}
                    </div>
                  )}
                </div>
                <form onSubmit={uploadInstaller} className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-installer-upload-form">
                  <input
                    type="file"
                    accept=".exe,application/x-msdownload,application/vnd.microsoft.portable-executable"
                    required
                    disabled={installerBusy}
                    onChange={(e) => setInstallerFile(e.target.files?.[0] || null)}
                    data-testid="admin-installer-file-input"
                    className="max-w-full font-mono text-xs file:mr-3 file:px-4 file:py-2 file:border file:border-border file:bg-transparent file:text-foreground file:font-mono file:text-[10px] file:tracking-[0.15em] file:uppercase hover:file:border-accent"
                  />
                  <button
                    type="submit"
                    disabled={!installerFile || installerBusy}
                    data-testid="admin-installer-upload-button"
                    className="shrink-0 flex items-center justify-center gap-2 px-5 py-2 bg-accent text-accent-foreground font-mono text-xs tracking-[0.18em] uppercase font-bold hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-50"
                  >
                    <Upload size={13} /> {installerBusy ? "Working..." : installerStatus?.source === "gridfs" ? "Replace" : "Upload"}
                  </button>
                  <button
                    type="button"
                    onClick={deleteInstaller}
                    disabled={installerBusy || installerStatus?.source !== "gridfs"}
                    data-testid="admin-installer-delete-button"
                    className="shrink-0 flex items-center justify-center gap-2 px-5 py-2 border border-red-500/40 text-red-500 font-mono text-xs tracking-[0.18em] uppercase hover:bg-red-500 hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-500"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </form>
              </div>
              {installerMessage && (
                <div className="px-6 md:px-8 py-4 font-mono text-xs tracking-[0.08em] text-muted-foreground" data-testid="admin-installer-message">
                  {installerMessage}
                </div>
              )}
            </section>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border mb-12"
              data-testid="admin-site-live-status"
            >
              <div className="bg-background p-6 md:p-8">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  <Clock3 size={13} className="text-accent" /> Site Live Since
                </div>
                <div className="font-mono text-sm md:text-base tracking-[0.08em] tabular-nums" data-testid="admin-site-live-since">
                  {formatIST(SITE_LIVE_SINCE)} IST
                </div>
              </div>
              <div className="bg-background p-6 md:p-8">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  Live For
                </div>
                <div className="font-display font-semibold text-3xl md:text-4xl tabular-nums" data-testid="admin-site-live-duration">
                  {formatLiveDuration(SITE_LIVE_SINCE, now)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-12">
              <div className="bg-background p-8" data-testid="admin-total-downloads">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  <Download size={13} className="text-accent" /> Total Downloads
                </div>
                <div className="font-display font-semibold text-5xl tabular-nums">{data.total}</div>
              </div>
              <div className="bg-background p-8" data-testid="admin-unique-ips">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  <Globe size={13} className="text-accent" /> Unique IPs
                </div>
                <div className="font-display font-semibold text-5xl tabular-nums">{data.unique_ips}</div>
              </div>
              <div className="bg-background p-8" data-testid="admin-unique-visitors">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  <Globe size={13} className="text-accent" /> Site Visitors
                </div>
                <div className="font-display font-semibold text-5xl tabular-nums">{data.unique_visitors ?? 0}</div>
              </div>
              <div className="bg-background p-8" data-testid="admin-total-visits">
                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  <Globe size={13} className="text-accent" /> Total Visits
                </div>
                <div className="font-display font-semibold text-5xl tabular-nums">{data.total_visits ?? 0}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Analytics logs">
              <button type="button" role="tab" aria-selected={activeLog === "downloads"} onClick={() => setActiveLog("downloads")} data-testid="analytics-tab-downloads" className={(activeLog === "downloads" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent") + " px-5 py-2.5 border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors"}>
                Downloads <span className="ml-2 opacity-70">{data.downloads?.length ?? 0}</span>
              </button>
              <button type="button" role="tab" aria-selected={activeLog === "visitors"} onClick={() => setActiveLog("visitors")} data-testid="analytics-tab-visitors" className={(activeLog === "visitors" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent") + " px-5 py-2.5 border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors"}>
                Visitors <span className="ml-2 opacity-70">{data.visits?.length ?? 0}</span>
              </button>
              <button type="button" role="tab" aria-selected={activeLog === "maintenance"} onClick={() => setActiveLog("maintenance")} data-testid="analytics-tab-maintenance" className={(activeLog === "maintenance" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent") + " px-5 py-2.5 border font-mono text-[10px] tracking-[0.18em] uppercase transition-colors"}>
                Maintenance Attempts <span className="ml-2 opacity-70">{data.maintenance_unique_visitors ?? 0}</span>
              </button>
            </div>

            {activeLog === "downloads" && (
              <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground" data-testid="downloads-table-heading">
                Download Log
              </h2>
              <button
                onClick={() => exportCsv("downloads")}
                data-testid="export-downloads-csv-button"
                className="flex items-center gap-2 px-4 py-1.5 border border-border font-mono text-[10px] tracking-[0.2em] uppercase hover:border-accent hover:text-accent transition-colors duration-300"
              >
                <FileDown size={12} /> Export CSV
              </button>
            </div>
            <div className="border border-border overflow-x-auto mb-12" data-testid="downloads-table">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">#</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">IP Address</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Location</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Timestamp (IST)</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium hidden md:table-cell">Browser / Device</th>
                  </tr>
                </thead>
                <tbody>
                  {data.downloads.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-muted-foreground tracking-[0.15em] uppercase" data-testid="downloads-empty">
                        No downloads recorded yet
                      </td>
                    </tr>
                  ) : (
                    data.downloads.map((d, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-200" data-testid={`download-row-${i}`}>
                        <td className="px-5 py-3.5 text-muted-foreground tabular-nums">{data.downloads.length - i}</td>
                        <td className="px-5 py-3.5 text-accent tabular-nums">{d.ip}</td>
                        <td className="px-5 py-3.5">{d.city ? `${d.city}, ${d.country}` : d.country || "—"}</td>
                        <td className="px-5 py-3.5 tabular-nums">{formatIST(d.timestamp)}</td>
                        <td className="px-5 py-3.5 text-muted-foreground max-w-[280px] truncate hidden md:table-cell" title={d.user_agent}>{browserInfo(d.user_agent)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

              </>
            )}

            {activeLog === "visitors" && (
              <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground" data-testid="visitors-table-heading">
                Visitor Log
              </h2>
              <button
                onClick={() => exportCsv("visits")}
                data-testid="export-visits-csv-button"
                className="flex items-center gap-2 px-4 py-1.5 border border-border font-mono text-[10px] tracking-[0.2em] uppercase hover:border-accent hover:text-accent transition-colors duration-300"
              >
                <FileDown size={12} /> Export CSV
              </button>
            </div>
            <div className="border border-border overflow-x-auto" data-testid="visitors-table">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">#</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">IP Address</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Location</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Visit Time (IST)</th>
                    <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium hidden md:table-cell">Browser / Device</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.visits ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-muted-foreground tracking-[0.15em] uppercase" data-testid="visitors-empty">
                        No visits recorded yet
                      </td>
                    </tr>
                  ) : (
                    data.visits.map((v, i) => (
                      <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-200" data-testid={`visit-row-${i}`}>
                        <td className="px-5 py-3.5 text-muted-foreground tabular-nums">{data.visits.length - i}</td>
                        <td className="px-5 py-3.5 text-accent tabular-nums">{v.ip}</td>
                        <td className="px-5 py-3.5">{v.city ? `${v.city}, ${v.country}` : v.country || "—"}</td>
                        <td className="px-5 py-3.5 tabular-nums">{formatIST(v.timestamp)}</td>
                        <td className="px-5 py-3.5 text-muted-foreground max-w-[280px] truncate hidden md:table-cell" title={v.user_agent}>{browserInfo(v.user_agent)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
              </>
            )}

            {activeLog === "maintenance" && (
              <section data-testid="maintenance-attempts-panel">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border mb-6">
                  <div className="bg-background p-6">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Unique Blocked Visitors</div>
                    <div className="font-display font-semibold text-4xl tabular-nums">{data.maintenance_unique_visitors ?? 0}</div>
                  </div>
                  <div className="bg-background p-6">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Total Access Sessions</div>
                    <div className="font-display font-semibold text-4xl tabular-nums">{data.maintenance_total_attempts ?? 0}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground" data-testid="maintenance-attempts-table-heading">Maintenance Access Attempts</h2>
                  <button onClick={() => exportCsv("maintenance-attempts")} data-testid="export-maintenance-attempts-csv-button" className="flex items-center gap-2 px-4 py-1.5 border border-border font-mono text-[10px] tracking-[0.2em] uppercase hover:border-accent hover:text-accent transition-colors duration-300">
                    <FileDown size={12} /> Export CSV
                  </button>
                </div>
                <div className="border border-border overflow-x-auto" data-testid="maintenance-attempts-table">
                  <table className="w-full font-mono text-xs">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">#</th>
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">IP Address</th>
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Location</th>
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Last Attempt (IST)</th>
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium">Sessions</th>
                        <th className="px-5 py-4 tracking-[0.2em] uppercase text-muted-foreground font-medium hidden md:table-cell">Browser / Device</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.maintenance_attempts ?? []).length === 0 ? (
                        <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground tracking-[0.15em] uppercase">No maintenance access attempts recorded yet</td></tr>
                      ) : (
                        data.maintenance_attempts.map((record, i) => (
                          <tr key={record.ip || i} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-200">
                            <td className="px-5 py-3.5 text-muted-foreground tabular-nums">{data.maintenance_attempts.length - i}</td>
                            <td className="px-5 py-3.5 text-accent tabular-nums">{record.ip}</td>
                            <td className="px-5 py-3.5">{record.city ? [record.city, record.country].filter(Boolean).join(", ") : record.country || "—"}</td>
                            <td className="px-5 py-3.5 tabular-nums">{formatIST(record.last_attempt_at)}</td>
                            <td className="px-5 py-3.5 tabular-nums">{record.attempt_count ?? 1}</td>
                            <td className="px-5 py-3.5 text-muted-foreground max-w-[280px] truncate hidden md:table-cell" title={record.user_agent}>{browserInfo(record.user_agent)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
